import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Toast.module.css';

export type ToastTone = 'neutral' | 'success' | 'danger';

export type ToastAction = {
  label: string;
  onClick: () => void;
  /** Optional keycap rendered next to the label, e.g. 'Z'. */
  keys?: string;
};

export type ToastOptions = {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Milliseconds on screen. 0 keeps it until dismissed. Default 5000. */
  duration?: number;
  /** Draw the remaining time as a ring — used by Undo Send. */
  countdown?: boolean;
  action?: ToastAction;
  onDismiss?: (reason: 'timeout' | 'user' | 'action') => void;
};

export type ToastHandle = {
  id: string;
  dismiss: () => void;
  update: (patch: Partial<ToastOptions>) => void;
};

export type ToastApi = {
  toast: (options: ToastOptions) => ToastHandle;
  dismiss: (id: string) => void;
  dismissAll: () => void;
};

type ToastRecord = ToastOptions & { id: string; createdAt: number; leaving: boolean };

/**
 * The store lives outside React: a queued send outlives the composer that
 * created it, so its countdown toast has to outlive that component too — and
 * any module can raise a toast without a provider being threaded to it.
 */
const DEFAULT_DURATION = 5000;
const EXIT_MS = 180;

let items: ToastRecord[] = [];
let nextId = 0;
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): ToastRecord[] {
  return items;
}

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function removeToast(id: string, reason: 'timeout' | 'user' | 'action'): void {
  clearTimer(id);
  const record = items.find((item) => item.id === id);
  if (!record || record.leaving) return;
  record.onDismiss?.(reason);
  items = items.map((item) => (item.id === id ? { ...item, leaving: true } : item));
  emit();
  setTimeout(() => {
    items = items.filter((item) => item.id !== id);
    emit();
  }, EXIT_MS);
}

function scheduleDismiss(id: string, duration: number): void {
  clearTimer(id);
  if (duration > 0) timers.set(id, setTimeout(() => removeToast(id, 'timeout'), duration));
}

export function toast(options: ToastOptions): ToastHandle {
  const id = `toast-${nextId++}`;
  const record: ToastRecord = {
    duration: DEFAULT_DURATION,
    tone: 'neutral',
    ...options,
    id,
    createdAt: Date.now(),
    leaving: false,
  };
  items = [...items, record];
  emit();
  scheduleDismiss(id, record.duration ?? DEFAULT_DURATION);

  return {
    id,
    dismiss: () => removeToast(id, 'user'),
    update: (patch) => {
      items = items.map((item) => (item.id === id ? { ...item, ...patch } : item));
      emit();
      if (patch.duration !== undefined) scheduleDismiss(id, patch.duration);
    },
  };
}

export function dismissToast(id: string): void {
  removeToast(id, 'user');
}

export function dismissAllToasts(): void {
  for (const item of items) removeToast(item.id, 'user');
}

const toastApi: ToastApi = { toast, dismiss: dismissToast, dismissAll: dismissAllToasts };

const ToastContext = createContext<ToastApi>(toastApi);

/**
 * Optional. The store works without it; the provider only guarantees a host in
 * a tree that renders no other Kiln surface.
 */
export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      <ToastOutlet />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/* ------------------------------- the outlet ------------------------------- */

let outletSeq = 0;
const mountedOutlets: number[] = [];
const outletListeners = new Set<() => void>();

function notifyOutlets(): void {
  for (const listener of outletListeners) listener();
}

function subscribeOutlets(listener: () => void): () => void {
  outletListeners.add(listener);
  return () => {
    outletListeners.delete(listener);
  };
}

function primaryOutlet(): number | null {
  return mountedOutlets[0] ?? null;
}

/**
 * Every long-lived Kiln surface renders an outlet, so the stack exists no
 * matter which of them is on screen; the first one mounted is the one that
 * draws, and the rest stay silent.
 */
export function ToastOutlet(): ReactElement | null {
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = ++outletSeq;

  useEffect(() => {
    const id = idRef.current;
    mountedOutlets.push(id);
    notifyOutlets();
    return () => {
      const index = mountedOutlets.indexOf(id);
      if (index >= 0) mountedOutlets.splice(index, 1);
      notifyOutlets();
    };
  }, []);

  const primary = useSyncExternalStore(subscribeOutlets, primaryOutlet, primaryOutlet);
  const stack = useSyncExternalStore(subscribe, snapshot, snapshot);

  if (primary !== idRef.current || typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.host} role="region" aria-label="Notifications">
      {stack.map((item) => (
        <ToastRow key={item.id} record={item} />
      ))}
    </div>,
    document.body,
  );
}

const RING_RADIUS = 7;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function ToastRow({ record }: { record: ToastRecord }): ReactElement {
  const duration = record.duration ?? DEFAULT_DURATION;
  const showRing = record.countdown === true && duration > 0;
  const [secondsLeft, setSecondsLeft] = useState(() => Math.ceil(duration / 1000));

  useEffect(() => {
    if (!showRing) return;
    const tick = () => {
      const remaining = duration - (Date.now() - record.createdAt);
      setSecondsLeft(Math.max(0, Math.ceil(remaining / 1000)));
    };
    tick();
    const interval = setInterval(tick, 250);
    return () => clearInterval(interval);
  }, [showRing, duration, record.createdAt]);

  const tone =
    record.tone === 'success' ? styles.success : record.tone === 'danger' ? styles.danger : styles.neutral;

  return (
    <div
      className={`${styles.toast} ${tone} ${record.leaving ? styles.leaving : ''}`}
      role="status"
      aria-live="polite"
    >
      {showRing ? (
        <span className={styles.ring} aria-hidden="true">
          {/* stroke-dashoffset is the only way to draw a real arc; it is scoped
              to an 18px SVG, so nothing else repaints. */}
          <svg viewBox="0 0 18 18" width="18" height="18">
            <circle className={styles.ringTrack} cx="9" cy="9" r={RING_RADIUS} />
            <circle
              className={styles.ringFill}
              cx="9"
              cy="9"
              r={RING_RADIUS}
              style={{ strokeDasharray: RING_CIRCUMFERENCE, animationDuration: `${duration}ms` }}
            />
          </svg>
          <span className={styles.ringCount}>{secondsLeft}</span>
        </span>
      ) : null}

      <div className={styles.body}>
        <span className={styles.title}>{record.title}</span>
        {record.description ? <span className={styles.description}>{record.description}</span> : null}
      </div>

      {record.action ? (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            record.action?.onClick();
            removeToast(record.id, 'action');
          }}
        >
          {record.action.label}
          {record.action.keys ? <kbd className={styles.kbd}>{record.action.keys}</kbd> : null}
        </button>
      ) : null}

      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notification"
        onClick={() => removeToast(record.id, 'user')}
      >
        <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
          <path d="M2 2l8 8M10 2l-8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

export default ToastOutlet;
